import {track} from '@getmoss/moss-analytics'
import {useCallback, useMemo} from 'react'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'

import type {Answer, Question} from './types'
import {getPersona} from './utils'

export type FraudGameQuestionAnalyticsType =
  | 'TRUE_FALSE'
  | 'FERMI'
  | 'SCENARIO_MCQ'
  | 'KNOWLEDGE_MCQ'
  | 'INVOICE_VS_MASTER_DATA'
  | 'PHISHING_EMAIL'
  | 'SPOT_THE_SCAM'

export type FraudGameShareType = 'copy_link' | 'linkedin' | 'whatsapp' | 'email'

export type FraudGameAnalyticsContext = {
  playerId: string
  gameRunId?: string
  referrerId?: string | null
  attemptNumber: number
}

type FraudGameAnalyticsContextOverride = Partial<FraudGameAnalyticsContext>

type TrackWithContext = {
  context?: FraudGameAnalyticsContextOverride
}

type InteractionType = 'click' | 'error'

export const createFraudGameAnalyticsId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return `fg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export const compactFraudGameAnalyticsParams = <T extends Record<string, unknown>>(params: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  ) as Partial<T>

export const buildFraudGameCommonParams = (context: FraudGameAnalyticsContext) => {
  const attemptNumber = context.attemptNumber ?? 0

  return compactFraudGameAnalyticsParams({
    visitor_id: context.playerId,
    game_run_id: context.gameRunId,
    player_id: context.playerId,
    referrer_id: context.referrerId,
    is_referred: Boolean(context.referrerId),
    attempt_number: attemptNumber,
    is_replay: attemptNumber > 1,
  })
}

export const getFraudGameQuestionAnalyticsType = (
  question: Pick<Question, 'type' | 'questionGroup'>,
): FraudGameQuestionAnalyticsType => {
  switch (question.type) {
    case 'true-false':
      return 'TRUE_FALSE'
    case 'fermi':
      return 'FERMI'
    case 'invoice-master-data':
      return 'INVOICE_VS_MASTER_DATA'
    case 'phishing-email':
      return 'PHISHING_EMAIL'
    case 'spot-the-scam':
      return 'SPOT_THE_SCAM'
    case 'multiple-choice':
      return question.questionGroup === 'p1-scenario-mcq' ? 'SCENARIO_MCQ' : 'KNOWLEDGE_MCQ'
    default:
      return 'KNOWLEDGE_MCQ'
  }
}

export const getFraudGameScoreTier = (score: number): string => {
  if (score >= 80) {
    return '80-100'
  }

  if (score >= 60) {
    return '60-79'
  }

  if (score >= 40) {
    return '40-59'
  }

  if (score >= 20) {
    return '20-39'
  }

  return '0-19'
}

export const getFraudGameEmailDomain = (email: string): string | undefined => {
  const normalizedEmail = email.trim().toLowerCase()
  const atIndex = normalizedEmail.lastIndexOf('@')

  if (atIndex === -1 || atIndex === normalizedEmail.length - 1) {
    return undefined
  }

  return normalizedEmail.slice(atIndex + 1)
}

export const buildFraudGameQuestionParams = ({
  question,
  questionNumber,
  totalQuestions,
}: {
  question: Question
  questionNumber: number
  totalQuestions: number
}) =>
  compactFraudGameAnalyticsParams({
    question_id: question.id,
    question_type: getFraudGameQuestionAnalyticsType(question),
    question_group: question.questionGroup,
    phase: question.phase,
    question_number: questionNumber,
    total_questions: totalQuestions,
    points_possible: question.points,
    timer_seconds: question.timerSeconds,
  })

const getMaxScore = (questions: Question[]): number => questions.reduce((sum, question) => sum + question.points, 0)

export const buildFraudGameResultParams = ({
  score,
  answers,
  questions,
  durationMs,
}: {
  score: number
  answers: Answer[]
  questions: Question[]
  durationMs?: number
}) =>
  compactFraudGameAnalyticsParams({
    total_score: score,
    max_score: getMaxScore(questions),
    solved_count: answers.filter((answer) => answer.correct).length,
    total_questions: questions.length,
    duration_ms: durationMs,
    score_tier: getFraudGameScoreTier(score),
    persona: getPersona(score).title,
  })

export const useFraudGameAnalytics = (context: FraudGameAnalyticsContext) => {
  const domainCountry = useGetDomainCountry()

  const basePageData = useMemo(
    () => ({
      type: 'fraud-game',
      domainCountry,
    }),
    [domainCountry],
  )

  const buildDetail = useCallback(
    (detail: Record<string, unknown> = {}, contextOverride?: FraudGameAnalyticsContextOverride) => {
      const resolvedContext: FraudGameAnalyticsContext = {
        playerId: contextOverride?.playerId ?? context.playerId,
        gameRunId: contextOverride?.gameRunId ?? context.gameRunId,
        referrerId: contextOverride?.referrerId ?? context.referrerId,
        attemptNumber: contextOverride?.attemptNumber ?? context.attemptNumber,
      }

      return compactFraudGameAnalyticsParams({
        ...buildFraudGameCommonParams(resolvedContext),
        ...detail,
      })
    },
    [context.attemptNumber, context.gameRunId, context.playerId, context.referrerId],
  )

  const trackInteraction = useCallback(
    (
      element: string,
      location: string,
      detail: Record<string, unknown>,
      contextOverride?: FraudGameAnalyticsContextOverride,
      type: InteractionType = 'click',
    ) => {
      track({
        event: 'interaction',
        interaction: {
          element,
          location,
          type,
          detail: buildDetail(detail, contextOverride),
        },
        page: basePageData,
      })
    },
    [basePageData, buildDetail],
  )

  const trackIntroViewed = useCallback(
    ({context: contextOverride}: TrackWithContext = {}) => {
      track({
        event: 'page_load',
        page: {
          type: 'fraud-game-intro',
          domainCountry,
          details: buildDetail({event_name: 'fraud_game_intro_viewed'}, contextOverride),
        },
      })
    },
    [buildDetail, domainCountry],
  )

  const trackTryYourselfViewed = useCallback(
    ({context: contextOverride}: TrackWithContext = {}) => {
      track({
        event: 'page_load',
        page: {
          type: 'fraud-game-try-yourself',
          domainCountry,
          details: buildDetail({event_name: 'fraud_game_try_yourself_viewed'}, contextOverride),
        },
      })
    },
    [buildDetail, domainCountry],
  )

  const trackGameStarted = useCallback(
    ({questions, context: contextOverride}: {questions: Question[]} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_started',
        'fraud-game-intro',
        {
          selected_question_ids: questions.map((question) => question.id),
          selected_question_types: questions.map(getFraudGameQuestionAnalyticsType),
          question_count: questions.length,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackQuestionViewed = useCallback(
    ({
      question,
      questionNumber,
      totalQuestions,
      context: contextOverride,
    }: {
      question: Question
      questionNumber: number
      totalQuestions: number
    } & TrackWithContext) => {
      trackInteraction(
        'fraud_game_question_viewed',
        'fraud-game-question',
        buildFraudGameQuestionParams({question, questionNumber, totalQuestions}),
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackQuestionAnswered = useCallback(
    ({
      question,
      questionNumber,
      totalQuestions,
      answerValue,
      score,
      correct,
      elapsedMs,
      remainingSeconds,
      timedOut,
      context: contextOverride,
    }: {
      question: Question
      questionNumber: number
      totalQuestions: number
      answerValue: string
      score: number
      correct: boolean
      elapsedMs: number
      remainingSeconds: number
      timedOut: boolean
    } & TrackWithContext) => {
      trackInteraction(
        'fraud_game_question_answered',
        'fraud-game-question',
        {
          ...buildFraudGameQuestionParams({question, questionNumber, totalQuestions}),
          answer_value: answerValue,
          score,
          is_correct: correct,
          elapsed_ms: elapsedMs,
          remaining_seconds: remainingSeconds,
          timed_out: timedOut,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackQuizCompleted = useCallback(
    ({
      score,
      answers,
      questions,
      durationMs,
      context: contextOverride,
    }: {
      score: number
      answers: Answer[]
      questions: Question[]
      durationMs: number
    } & TrackWithContext) => {
      trackInteraction(
        'fraud_game_quiz_completed',
        'fraud-game-quiz',
        buildFraudGameResultParams({score, answers, questions, durationMs}),
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackDetailsPromptViewed = useCallback(
    ({score, context: contextOverride}: {score: number} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_player_details_prompt_viewed',
        'fraud-game-results',
        {
          total_score: score,
          score_tier: getFraudGameScoreTier(score),
          persona: getPersona(score).title,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackDetailsFormOpened = useCallback(
    ({score, context: contextOverride}: {score: number} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_player_details_form_opened',
        'fraud-game-results',
        {total_score: score},
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackCompetitionEntrySubmitted = useCallback(
    ({
      score,
      playerId,
      email,
      marketingOptIn,
      context: contextOverride,
    }: {
      score: number
      playerId: string
      email: string
      marketingOptIn?: boolean
    } & TrackWithContext) => {
      track({
        event: 'form_submission',
        form: {
          formName: 'fraud_game_competition_entry',
          funnelId: playerId,
          intent: 'competition_entry',
          stepName: 'player_details_submitted',
        },
        page: {
          ...basePageData,
          details: buildDetail(
            {
              total_score: score,
              email_domain: getFraudGameEmailDomain(email),
              marketing_opt_in: marketingOptIn,
            },
            {...contextOverride, playerId},
          ),
        },
      })
    },
    [basePageData, buildDetail],
  )

  const trackDetailsSubmitFailed = useCallback(
    ({score, errorMessage, context: contextOverride}: {score: number; errorMessage: string} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_player_details_submit_failed',
        'fraud-game-results',
        {
          total_score: score,
          error_message: errorMessage,
        },
        contextOverride,
        'error',
      )
    },
    [trackInteraction],
  )

  const trackLeaderboardViewed = useCallback(
    ({
      score,
      rank,
      totalPlayers,
      context: contextOverride,
    }: {
      score: number
      rank: number
      totalPlayers: number
    } & TrackWithContext) => {
      trackInteraction(
        'fraud_game_leaderboard_viewed',
        'fraud-game-results',
        {
          total_score: score,
          rank,
          total_players: totalPlayers,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackShareModalOpened = useCallback(
    ({score, context: contextOverride}: {score: number} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_share_modal_opened',
        'fraud-game-results',
        {
          total_score: score,
          persona: getPersona(score).title,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackShareClicked = useCallback(
    ({
      shareType,
      shareUrl,
      score,
      context: contextOverride,
    }: {
      shareType: FraudGameShareType
      shareUrl: string
      score: number
    } & TrackWithContext) => {
      trackInteraction(
        'fraud_game_share_clicked',
        'fraud-game-share-modal',
        {
          share_type: shareType,
          is_external_share: shareType !== 'copy_link',
          share_url: shareUrl,
          total_score: score,
          persona: getPersona(score).title,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  const trackReplayClicked = useCallback(
    ({previousScore, context: contextOverride}: {previousScore: number} & TrackWithContext) => {
      trackInteraction(
        'fraud_game_replay_clicked',
        'fraud-game-results',
        {
          previous_score: previousScore,
        },
        contextOverride,
      )
    },
    [trackInteraction],
  )

  return useMemo(
    () => ({
      trackIntroViewed,
      trackTryYourselfViewed,
      trackGameStarted,
      trackQuestionViewed,
      trackQuestionAnswered,
      trackQuizCompleted,
      trackDetailsPromptViewed,
      trackDetailsFormOpened,
      trackCompetitionEntrySubmitted,
      trackDetailsSubmitFailed,
      trackLeaderboardViewed,
      trackShareModalOpened,
      trackShareClicked,
      trackReplayClicked,
    }),
    [
      trackCompetitionEntrySubmitted,
      trackDetailsFormOpened,
      trackDetailsPromptViewed,
      trackDetailsSubmitFailed,
      trackGameStarted,
      trackIntroViewed,
      trackLeaderboardViewed,
      trackQuestionAnswered,
      trackQuestionViewed,
      trackQuizCompleted,
      trackReplayClicked,
      trackShareClicked,
      trackShareModalOpened,
      trackTryYourselfViewed,
    ],
  )
}
